import { useState } from "react";
import { Link } from "react-router-dom";

import { Badge, Drawer } from "@/design-system/primitives";
import {
  TICKET_STATES,
  TICKET_STATE_LABELS,
  ticketDisplaySubject,
  useAddTicketNote,
  useAdminRoster,
  useEscalateTicket,
  useResolveMissingInfoTicket,
  useResolveTicket,
  useTicket,
  useUpdateTicket,
} from "@/lib/api/ticketHooks";

import {
  DISPOSITION_TONE,
  ageLabel,
  isOverdue,
  canSetGenericTicketState,
} from "./ticketUi";
import type { ClinicalAgentContext } from "../clinical-admin/ClinicalAgentDrawer";
import { MissingInfoIntakeContext } from "./MissingInfoIntakeContext";

/** Ticket detail drawer — order/warning context + ops actions (assign, move,
 *  escalate to clinician, resolve, note) + activity timeline. */
export function TicketDrawer({
  ticketId,
  onClose,
  onOpenPatient,
  onOpenAgent,
}: {
  ticketId: string | null;
  onClose: () => void;
  onOpenPatient?: (patientId: string) => void;
  onOpenAgent?: (context: ClinicalAgentContext) => void;
}) {
  const { data: ticket, isLoading } = useTicket(ticketId);
  const roster = useAdminRoster();
  const update = useUpdateTicket();
  const resolve = useResolveTicket();
  const resolveMissingInfo = useResolveMissingInfoTicket();
  const escalate = useEscalateTicket();
  const addNote = useAddTicketNote();
  const [note, setNote] = useState("");
  const [resolveNote, setResolveNote] = useState("");
  const [missingInfoNote, setMissingInfoNote] = useState("");
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalationNote, setEscalationNote] = useState("");

  const open = !!ticketId;
  const overdue = isOverdue(ticket?.sla_due_at);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={ticket?.ticket_number ?? "Ticket"}
    >
      {!ticket || isLoading ? (
        <div className="p-15 type-label-sm text-ink-muted">Loading…</div>
      ) : (
        <div className="p-15 flex flex-col gap-18">
          {/* Header badges */}
          <div className="flex items-center gap-6 flex-wrap">
            <Badge
              tone={DISPOSITION_TONE[ticket.disposition] ?? "neutral"}
              size="sm"
            >
              {ticket.disposition}
            </Badge>
            <Badge tone={overdue ? "danger" : "neutral"} size="sm">
              {overdue ? "overdue" : ageLabel(ticket.first_flagged_at)}
            </Badge>
            <Badge tone="neutral" size="sm">
              {ticket.source}
            </Badge>
            {ticket.reason_code && (
              <Badge tone="neutral" size="sm">
                {ticket.reason_code}
              </Badge>
            )}
          </div>

          <div>
            <div className="type-label-md font-semibold leading-snug">
              {ticketDisplaySubject(ticket)}
            </div>
            {ticket.body && (
              <p className="mt-8 type-label-sm text-ink-secondary whitespace-pre-wrap">
                {ticket.body}
              </p>
            )}
          </div>

          {ticket.patient_id &&
            (onOpenPatient ? (
              <button
                type="button"
                onClick={() => onOpenPatient(ticket.patient_id as string)}
                className="type-label-sm text-accent hover:underline text-left"
              >
                Preview patient →
              </button>
            ) : (
              <Link
                to={`/admin/clinical-admin/customers/${ticket.patient_id}`}
                className="type-label-sm text-accent hover:underline"
              >
                Open patient →
              </Link>
            ))}

          {ticket.source === "missing_info" && (
            <section className="border border-warning/30 bg-warning-wash p-12 flex flex-col gap-9">
              <div>
                <p className="font-mono text-10 uppercase text-warning">
                  Clinician missing info
                </p>
                <p className="type-label-md mt-4">
                  {ticket.missing_info_reason_label ||
                    ticket.reason_code ||
                    "Patient information"}
                </p>
                {ticket.missing_info_details && (
                  <p className="type-label-sm text-ink-secondary mt-5 whitespace-pre-wrap">
                    {ticket.missing_info_details}
                  </p>
                )}
                {ticket.missing_info_intake_redo && (
                  <p className="type-label-sm text-ink-secondary mt-5">
                    The patient was asked to complete a new intake (link sent by
                    email/SMS; the form is back in their portal). This hold clears
                    on its own when it comes in — resolve manually only if you
                    verified the answers another way.
                  </p>
                )}
              </div>
              {(ticket.missing_info_findings?.length ?? 0) > 0 && (
                <div>
                  <p className="font-mono text-10 uppercase text-ink-muted">
                    Programmatic intake checks the clinician saw
                  </p>
                  <ul className="mt-5 flex flex-col gap-4 list-disc pl-16">
                    {ticket.missing_info_findings!.map((finding) => (
                      <li
                        key={finding}
                        className="type-label-sm text-ink-secondary"
                      >
                        {finding}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {ticket.patient_id && (
                <div className="flex flex-col gap-5">
                  <Link
                    to={
                      ticket.admin_intake_path ??
                      `/admin/clinical-admin/customers/${ticket.patient_id}?tab=intake`
                    }
                    className="type-label-sm text-accent hover:underline"
                  >
                    Open patient intake →
                  </Link>
                  {ticket.clinician_path && (
                    <Link
                      to={ticket.clinician_path}
                      className="type-label-sm text-ink-muted hover:underline"
                    >
                      Open in clinician portal (clinician access) →
                    </Link>
                  )}
                </div>
              )}
              {ticket.patient_id && (
                <MissingInfoIntakeContext
                  patientId={ticket.patient_id}
                  orderId={ticket.pharmacy_order_id ?? null}
                />
              )}
              <div>
                <label className="font-mono text-10 uppercase text-ink-muted">
                  What was supplied or corrected?
                  <textarea
                    value={missingInfoNote}
                    onChange={(event) => setMissingInfoNote(event.target.value)}
                    placeholder="Resolution note for the clinician"
                    rows={3}
                    className="mt-5 type-label-sm px-10 py-8 rounded border border-line bg-raised text-ink w-full normal-case font-sans"
                  />
                </label>
                <button
                  type="button"
                  onClick={() =>
                    resolveMissingInfo.mutate(
                      { id: ticket.id, note: missingInfoNote.trim() },
                      {
                        onSuccess: () => {
                          setMissingInfoNote("");
                          onClose();
                        },
                      },
                    )
                  }
                  disabled={
                    resolveMissingInfo.isPending || !missingInfoNote.trim()
                  }
                  className="mt-8 type-label-sm px-12 py-8 rounded border border-warning bg-raised hover:bg-warning-wash disabled:opacity-50"
                >
                  {resolveMissingInfo.isPending
                    ? "Returning to clinician…"
                    : "Resolve & return to clinician"}
                </button>
                {resolveMissingInfo.isError && (
                  <p className="type-label-sm text-danger mt-6">
                    Could not resolve the blocker. It may already be closed.
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Assignment */}
          <Section label="Assignee">
            <div className="flex items-center gap-8">
              <select
                value={ticket.assigned_to ?? ""}
                onChange={(e) =>
                  e.target.value
                    ? update.mutate({
                        id: ticket.id,
                        assigned_to: e.target.value,
                      })
                    : update.mutate({ id: ticket.id, unassign: true })
                }
                className="type-label-sm px-10 py-8 rounded border border-line bg-raised flex-1"
              >
                <option value="">Unassigned</option>
                {(roster.data?.admins ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.display_name || a.email || a.id}
                  </option>
                ))}
              </select>
            </div>
          </Section>

          {/* State */}
          <Section label="Status">
            <div className="flex items-center gap-8 flex-wrap">
              {TICKET_STATES.filter((s) =>
                canSetGenericTicketState(ticket, s),
              ).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => update.mutate({ id: ticket.id, state: s })}
                  className={`type-label-sm px-10 py-6 rounded border ${
                    ticket.state === s
                      ? "border-accent bg-accent-wash text-accent"
                      : "border-line hover:bg-raised"
                  }`}
                >
                  {TICKET_STATE_LABELS[s]}
                </button>
              ))}
            </div>
          </Section>

          {/* Actions */}
          {ticket.source === "missing_info" && (
            <p className="type-label-sm text-ink-muted">
              Verify the missing information, then use Resolve &amp; return to
              clinician above. Changing ticket status alone cannot clear the
              clinical hold.
            </p>
          )}
          {ticket.source !== "missing_info" && (
            <Section label="Actions">
              <div className="flex items-center gap-8 flex-wrap">
                <button
                  type="button"
                  onClick={() =>
                    update.mutate({ id: ticket.id, state: "fixed" })
                  }
                  disabled={update.isPending}
                  className="type-label-sm px-10 py-8 rounded border border-accent bg-accent-wash text-accent hover:opacity-90 disabled:opacity-50"
                >
                  Mark fixed
                </button>
                {!escalateOpen && (
                  <button
                    type="button"
                    onClick={() => setEscalateOpen(true)}
                    disabled={Boolean(ticket.clinician_task_id)}
                    className="type-label-sm px-10 py-8 rounded border border-line hover:bg-raised disabled:opacity-50"
                  >
                    {ticket.clinician_task_id
                      ? "Escalated to clinician"
                      : "Escalate to clinician"}
                  </button>
                )}
                {onOpenAgent ? (
                  <button
                    type="button"
                    onClick={() =>
                      onOpenAgent({
                        patientName: ticket.patient_name,
                        patientEmail: ticket.patient_email,
                        orderId: ticket.pharmacy_order_id,
                        orderNumber: ticket.order_number,
                        ticketNumber: ticket.ticket_number,
                        ticketSubject: ticketDisplaySubject(ticket),
                      })
                    }
                    className="type-label-sm px-10 py-8 rounded border border-line hover:bg-raised"
                  >
                    Ask Clinical AI
                  </button>
                ) : null}
              </div>
              {escalateOpen && !ticket.clinician_task_id ? (
                <div className="mt-10 border border-line bg-raised p-12 rounded">
                  <label className="font-mono text-10 uppercase text-ink-muted">
                    Context for the clinician (optional)
                    <textarea
                      autoFocus
                      value={escalationNote}
                      onChange={(event) =>
                        setEscalationNote(event.target.value)
                      }
                      placeholder="Add relevant patient or ticket context…"
                      rows={3}
                      className="mt-5 type-label-sm px-10 py-8 rounded border border-line bg-page text-ink w-full normal-case font-sans"
                    />
                  </label>
                  <div className="mt-8 flex items-center justify-end gap-8">
                    <button
                      type="button"
                      onClick={() => {
                        setEscalateOpen(false);
                        setEscalationNote("");
                      }}
                      disabled={escalate.isPending}
                      className="type-label-sm px-10 py-7 rounded border border-line hover:bg-page disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        escalate.mutate(
                          {
                            id: ticket.id,
                            note: escalationNote.trim() || undefined,
                          },
                          {
                            onSuccess: () => {
                              setEscalateOpen(false);
                              setEscalationNote("");
                            },
                          },
                        )
                      }
                      disabled={escalate.isPending}
                      className="type-label-sm px-10 py-7 rounded bg-ink text-page hover:opacity-90 disabled:opacity-50"
                    >
                      {escalate.isPending
                        ? "Creating clinician task…"
                        : "Confirm escalation"}
                    </button>
                  </div>
                </div>
              ) : null}
              {escalate.isError ? (
                <p className="type-label-sm text-danger mt-8">
                  Escalation failed. This ticket needs a patient with a linked
                  Healthie chart.
                </p>
              ) : null}
            </Section>
          )}

          {/* Force resolve (override) — tickets auto-resolve when the underlying
              condition clears (warning gone / order progressed). */}
          {ticket.source !== "missing_info" && (
            <Section label="Force resolve (override)">
              <p className="text-10 text-ink-muted leading-snug">
                Tickets auto-resolve when the condition clears — the warning is
                resolved, or the order progresses to its next step. Use this
                only for false positives or cancellations. To signal you&apos;ve
                acted, use <strong>Mark fixed</strong> above.
              </p>
              <textarea
                value={resolveNote}
                onChange={(e) => setResolveNote(e.target.value)}
                placeholder="Reason for force-resolving"
                rows={2}
                required
                aria-required="true"
                className="mt-8 type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
              />
              <button
                type="button"
                onClick={() =>
                  resolve.mutate(
                    { id: ticket.id, note: resolveNote.trim() },
                    {
                      onSuccess: () => {
                        setResolveNote("");
                        onClose();
                      },
                    },
                  )
                }
                disabled={resolve.isPending || !resolveNote.trim()}
                className="mt-8 type-label-sm px-12 py-8 rounded border border-line hover:bg-raised disabled:opacity-50"
              >
                {resolve.isPending ? "Resolving…" : "Force resolve"}
              </button>
              {resolve.isError ? (
                <p className="type-label-sm text-danger mt-8">
                  Could not force-resolve this ticket. A reason is required.
                </p>
              ) : null}
            </Section>
          )}

          {/* Add note */}
          <Section label="Notes">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note…"
              rows={2}
              className="type-label-sm px-10 py-8 rounded border border-line bg-raised w-full"
            />
            <button
              type="button"
              onClick={() =>
                note.trim() &&
                addNote.mutate(
                  { id: ticket.id, note: note.trim() },
                  { onSuccess: () => setNote("") },
                )
              }
              disabled={!note.trim() || addNote.isPending}
              className="mt-8 type-label-sm px-12 py-8 rounded border border-line hover:bg-raised disabled:opacity-50"
            >
              Add note
            </button>
          </Section>

          {/* Activity — actor names + humanized values come enriched from the
              backend (raw ids stay in the DB; labels attach at read time). */}
          <Section label="Activity">
            <ul className="flex flex-col gap-8">
              {(ticket.events ?? []).map((ev) => {
                const from = ev.from_value_label ?? ev.from_value;
                const to = ev.to_value_label ?? ev.to_value;
                return (
                  <li key={ev.id} className="text-10 text-ink-muted flex gap-8">
                    <span className="font-mono shrink-0">
                      {new Date(ev.created_at).toLocaleString()}
                    </span>
                    <span>
                      <span className="font-semibold">
                        {ev.actor_name || "system"}
                      </span>{" "}
                      {ev.event_type.replace(/_/g, " ")}
                      {from || to
                        ? `: ${from ?? ""}${from && to ? " → " : ""}${to ?? ""}`
                        : ""}
                      {ev.note ? ` — ${ev.note}` : ""}
                    </span>
                  </li>
                );
              })}
              {(ticket.events ?? []).length === 0 && (
                <li className="text-10 text-ink-muted">No activity yet.</li>
              )}
            </ul>
          </Section>
        </div>
      )}
    </Drawer>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-8">
      <div className="font-mono text-10 uppercase text-ink-muted">{label}</div>
      {children}
    </div>
  );
}
